import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { useLanguage } from "@/context/LanguageContext";
import { scrollToId } from "@/lib/scroll";

const SECTIONS = ["home", "booking", "albums", "videos", "gallery", "contact"];

export const Navbar = () => {
  const { lang, setLang, t } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setMobileOpen(false);
    if (location.pathname !== "/") {
      navigate("/", { state: { scrollTo: id } });
    } else {
      scrollToId(id);
    }
  };

  return (
    <>
      <header
        data-testid="navbar"
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${scrolled ? "gc-glass border-b border-ivory/10" : "bg-transparent"}`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 h-[72px] flex items-center justify-between">
          <button data-testid="nav-logo" onClick={() => go("home")} className="flex items-center gap-3 group">
            <span className="w-9 h-9 border border-gold/60 flex items-center justify-center font-serif text-gold text-lg group-hover:bg-gold group-hover:text-ivory transition-colors duration-300">
              GC
            </span>
            <span className="hidden sm:block font-serif tracking-[0.18em] text-sm uppercase text-ivory">Giovanni Costello</span>
          </button>

          <nav className="hidden lg:flex items-center gap-7">
            {SECTIONS.map((s) => (
              <button
                key={s}
                data-testid={`nav-link-${s}`}
                onClick={() => go(s)}
                className="text-[11px] tracking-[0.22em] uppercase text-ash hover:text-gold transition-colors duration-300"
              >
                {t.nav[s]}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-4">
            <div className="flex items-center border border-ivory/15 text-[10px] tracking-[0.2em]">
              {["de", "en", "it"].map((l) => (
                <button
                  key={l}
                  data-testid={`lang-switch-${l}`}
                  onClick={() => setLang(l)}
                  className={`px-2.5 py-1.5 transition-colors duration-300 ${lang === l ? "bg-gold text-ivory" : "text-dim hover:text-ivory"}`}
                >
                  {l.toUpperCase()}
                </button>
              ))}
            </div>

            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setMobileOpen(true)}
              className="lg:hidden p-2 text-ivory"
              aria-label="Menu"
            >
              <Menu className="w-5 h-5" strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[80] gc-glass flex flex-col"
          >
            <div className="flex justify-end p-5">
              <button data-testid="mobile-menu-close" onClick={() => setMobileOpen(false)} className="p-2 text-ivory" aria-label="Close">
                <X className="w-6 h-6" strokeWidth={1.25} />
              </button>
            </div>
            <nav className="flex-1 flex flex-col items-center justify-center gap-6">
              {SECTIONS.map((s, i) => (
                <motion.button
                  key={s}
                  data-testid={`mobile-nav-link-${s}`}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.05 * i, duration: 0.4 }}
                  onClick={() => go(s)}
                  className="font-serif text-3xl text-ivory hover:text-gold transition-colors duration-300"
                >
                  {t.nav[s]}
                </motion.button>
              ))}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
